import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp, ROLE_HOME } from "../context/AppContext";
import { track } from "../lib/track";
import { BrandLogo, SignalMark } from "../components/Logo";
import { LogIn, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={ROLE_HOME[user.role] || "/today"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@") || password.length < 4) {
      setError("Enter your email and password");
      return;
    }
    setBusy(true);
    try {
      const u = await login(email, password);
      track("login_success", "/login");
      navigate(ROLE_HOME[u.role] || "/today", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="login-page">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: "#0B1F3A" }}>
        <div className="absolute inset-0 opacity-[0.14] bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)" }} />
        <Link to="/" className="relative"><BrandLogo light tagline="Intelligence Platform" /></Link>
        <div className="relative max-w-md">
          <h1 className="text-3xl font-semibold tracking-tight leading-tight">
            Every scientific conversation becomes strategic intelligence.
          </h1>
          <p className="mt-4 text-white/55 leading-relaxed">
            Prepare · Meet · Capture · Follow up · Learn · Intelligence
          </p>
        </div>
        <p className="relative text-xs text-white/40">© 2026 MA Signals™ · Approved users only</p>
      </div>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12 bg-white text-[#0B1F3A]">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm mx-auto">
          <div className="lg:hidden mb-10"><SignalMark size={44} /></div>
          <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="text-sm text-[#0B1F3A]/55 mt-2 mb-8">
            Access is provisioned for approved pilot users.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-[#0B1F3A]/50 mb-1.5">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="w-full rounded-xl border border-[#0B1F3A]/15 px-4 py-3 text-sm outline-none focus:border-[#1F6FEB] focus:ring-1 focus:ring-[#1F6FEB] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-[#0B1F3A]/50 mb-1.5">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full rounded-xl border border-[#0B1F3A]/15 px-4 py-3 text-sm outline-none focus:border-[#1F6FEB] focus:ring-1 focus:ring-[#1F6FEB] transition-colors"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5">{error}</p>
            )}

            <div className="flex justify-end">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-[#1F6FEB] hover:underline">
                Forgot password?
              </Link>
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1F6FEB] text-white py-3.5 text-sm font-semibold hover:bg-[#1a5fd0] transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <LogIn className="h-4 w-4" strokeWidth={2} />}
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-8 flex items-start gap-2 text-xs text-[#0B1F3A]/45 leading-relaxed">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.5} />
            Need access? Ask your MA Signals administrator, or book a demo from the website.
          </p>

          <Link to="/" data-testid="login-back-btn" className="mt-10 inline-flex items-center gap-1.5 text-sm text-[#0B1F3A]/55 hover:text-[#0B1F3A] transition-colors">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to website
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
